import { useState, useCallback, useMemo } from 'react'
import { cn } from '@utils'
import { useSessionStore } from '@/stores/useSessionStore'
import { getCountries, getCountryStates, isCountryUSOrCA, formatZipField } from '@utils/validation'

const errorClass = 'border-red-500! focus:border-red-500!'

enum VerificationMethod {
  FullName = 1,
  LastName = 2,
  Street = 3,
  Zip = 4,
  Address = 5,
  BillingStreet = 6,
  BillingZip = 7,
  BillingAddress = 8,
  Phone = 1000,
  PartialPhone = 1001,
  Email = 1002,
  PartialEmail = 1003,
}

const VerificationMethodFields: Record<VerificationMethod, string[]> = {
  [VerificationMethod.FullName]: ['first_name', 'last_name'],
  [VerificationMethod.LastName]: ['last_name'],
  [VerificationMethod.Street]: ['address'],
  [VerificationMethod.Zip]: ['zip'],
  [VerificationMethod.Address]: ['address', 'city', 'state', 'zip', 'country'],
  [VerificationMethod.BillingStreet]: ['address'],
  [VerificationMethod.BillingZip]: ['zip'],
  [VerificationMethod.BillingAddress]: ['address', 'city', 'state', 'zip', 'country'],
  [VerificationMethod.Phone]: ['phone'],
  [VerificationMethod.PartialPhone]: ['phone'],
  [VerificationMethod.Email]: ['email'],
  [VerificationMethod.PartialEmail]: ['email'],
}

interface VerificationFieldProps {
  method: VerificationMethod | VerificationMethod[]
  depth?: number
  allFieldsRequired?: boolean
  value?: {
    first_name?: string
    last_name?: string
    address?: string
    city?: string
    state?: string
    zip?: string
    phone?: string
    email?: string
    country?: string
  }
  placeholders?: Record<string, string>
  onUpdateData?: (field: string, value: string) => void
  error?: boolean
}

/* Shared form field component */
const Field = ({
  id,
  label,
  type = 'text',
  value,
  placeholder,
  required,
  title,
  error,
  onChange,
}: {
  id: string
  label: string
  type?: string
  value: string
  placeholder: string
  required?: boolean
  title?: string
  error?: boolean
  onChange: (val: string) => void
}) => (
  <div className="w-full">
    <label htmlFor={id} className="block text-base font-medium mb-1" style={{ color: 'var(--text-color)' }}>
      {label}
    </label>
    <input
      id={id}
      type={type}
      className={cn('form-control w-full', error && errorClass)}
      value={value}
      placeholder={placeholder}
      onChange={e => onChange(e.target.value)}
      required={required}
      title={title}
    />
  </div>
)

const VerificationField = ({
  method,
  depth = 0,
  allFieldsRequired = false,
  value = {
    first_name: '',
    last_name: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    phone: '',
    email: '',
    country: 'US',
  },
  placeholders = {},
  onUpdateData,
  error = false,
}: VerificationFieldProps) => {
  const campaign = useSessionStore(s => s.campaign)

  const [fields, setFields] = useState(value)

  const isMethodGroup = Array.isArray(method)

  // Individual field type checks
  const isFirstName = method === VerificationMethod.FullName
  const isLastName = method === VerificationMethod.LastName || method === VerificationMethod.FullName
  const isStreet = [
    VerificationMethod.BillingStreet,
    VerificationMethod.BillingAddress,
    VerificationMethod.Street,
    VerificationMethod.Address,
  ].includes(method as VerificationMethod)
  const isFullAddress = [VerificationMethod.BillingAddress, VerificationMethod.Address].includes(
    method as VerificationMethod
  )
  const isZip = [
    VerificationMethod.BillingZip,
    VerificationMethod.BillingAddress,
    VerificationMethod.Zip,
    VerificationMethod.Address,
  ].includes(method as VerificationMethod)
  const isBilling = [
    VerificationMethod.BillingZip,
    VerificationMethod.BillingAddress,
    VerificationMethod.BillingStreet,
  ].includes(method as VerificationMethod)
  const isEmail = [VerificationMethod.Email, VerificationMethod.PartialEmail].includes(method as VerificationMethod)
  const isPhone = [VerificationMethod.Phone, VerificationMethod.PartialPhone].includes(method as VerificationMethod)

  const countries = useMemo(() => {
    const countryList = getCountries()
    return [{ value: '', text: 'Country' }, ...countryList.map(c => ({ value: c.value, text: c.text }))]
  }, [])

  const getStates = useCallback(() => {
    return getCountryStates(campaign, fields.country || 'US')
  }, [fields.country, campaign])

  const isUSOrCA = useCallback(() => isCountryUSOrCA(fields.country || 'US'), [fields.country])

  const zipFormat = useCallback((val: string) => formatZipField(val, fields.country || 'US'), [fields.country])

  const isFieldRequired = useMemo(
    () => allFieldsRequired || (Object.values(value).every(v => !v) && Object.values(fields).every(v => !v)),
    [allFieldsRequired, value, fields]
  )

  const hasAnyFieldValue = useMemo(() => Object.values(fields).some(v => !!v), [fields])

  const handleUpdateData = useCallback(
    (field: string, val: string) => {
      setFields(prev => {
        const newFields = { ...prev, [field]: val }
        if (field === 'country') {
          newFields.state = ''
          newFields.zip = ''
        }
        return newFields
      })
      onUpdateData?.(field, val)
    },
    [onUpdateData]
  )

  const hasFieldValue = useCallback(
    (methodToCheck: VerificationMethod | VerificationMethod[]): boolean => {
      const methods = Array.isArray(methodToCheck) ? methodToCheck : [methodToCheck]
      return methods.some(m =>
        (VerificationMethodFields[m] || []).some(field => !!fields[field as keyof typeof fields])
      )
    },
    [fields]
  )

  const hasAnyOtherFieldValue = useCallback(
    (methodToCheck: VerificationMethod): boolean => {
      if (!isMethodGroup) {
        return false
      }
      return (method as VerificationMethod[]).some(m => m !== methodToCheck && hasFieldValue(m))
    },
    [isMethodGroup, method, hasFieldValue]
  )

  const showMethod = useCallback(
    (methodToCheck: VerificationMethod): boolean => !hasAnyOtherFieldValue(methodToCheck),
    [hasAnyOtherFieldValue]
  )

  const [lastValue, setLastValue] = useState(value)
  if (value !== lastValue) {
    setLastValue(value)
    setFields({ ...value })
  }

  // Method group — multiple verification methods with "or" separator
  if (isMethodGroup) {
    const indentStyle = depth > 0 ? { transform: `translateX(${depth * 50}px)` } : undefined
    return (
      <div className="space-y-0" style={indentStyle}>
        {(method as VerificationMethod[]).map((singleMethod, index) => (
          <div key={`verification-field-${depth}-${index}`}>
            {index > 0 && !hasAnyFieldValue && (
              <div className="field-or-divider">
                <span>or</span>
              </div>
            )}
            {showMethod(singleMethod) && (
              <VerificationField
                method={singleMethod}
                value={fields}
                placeholders={placeholders}
                depth={depth + 1}
                allFieldsRequired={allFieldsRequired}
                onUpdateData={handleUpdateData}
                error={error}
              />
            )}
          </div>
        ))}
      </div>
    )
  }

  // Single field render
  return (
    <div className="space-y-4">
      {isFirstName && (
        <Field
          id="first_name"
          label="First Name"
          value={fields.first_name || ''}
          placeholder={placeholders.first_name || 'First Name'}
          required={isFieldRequired}
          error={error}
          onChange={val => handleUpdateData('first_name', val)}
        />
      )}

      {isLastName && (
        <Field
          id="last_name"
          label="Last Name"
          value={fields.last_name || ''}
          placeholder={placeholders.last_name || 'Last Name'}
          required={isFieldRequired}
          error={error}
          onChange={val => handleUpdateData('last_name', val)}
        />
      )}

      {isFullAddress && (
        <>
          <Field
            id="address"
            label="Address"
            value={fields.address || ''}
            placeholder={placeholders.address || 'Address'}
            error={error}
            onChange={val => handleUpdateData('address', val)}
          />
          <Field
            id="city"
            label="City"
            value={fields.city || ''}
            placeholder={placeholders.city || 'City'}
            error={error}
            onChange={val => handleUpdateData('city', val)}
          />

          <div className="w-full">
            <label
              htmlFor="country"
              className="block text-base font-medium mb-1"
              style={{ color: 'var(--text-color)' }}>
              Country
            </label>
            <select
              id="country"
              className={cn('form-control w-full', error && errorClass)}
              value={fields.country || 'US'}
              onChange={e => handleUpdateData('country', e.target.value)}>
              {countries.map(country => (
                <option key={country.value} value={country.value}>
                  {country.text}
                </option>
              ))}
            </select>
          </div>

          <div className="w-full">
            <label htmlFor="state" className="block text-base font-medium mb-1" style={{ color: 'var(--text-color)' }}>
              State
            </label>
            {isUSOrCA() ? (
              <select
                id="state"
                className={cn('form-control w-full', error && errorClass)}
                value={fields.state || ''}
                onChange={e => handleUpdateData('state', e.target.value)}>
                <option value="">Select State</option>
                {getStates().map(state => (
                  <option key={state.value} value={state.value}>
                    {state.text}
                  </option>
                ))}
              </select>
            ) : (
              <input
                id="state"
                type="text"
                className={cn('form-control w-full', error && errorClass)}
                value={fields.state || ''}
                placeholder={placeholders.state || 'State'}
                onChange={e => handleUpdateData('state', e.target.value)}
              />
            )}
          </div>

          <Field
            id="zip"
            label="Zip"
            value={fields.zip || ''}
            placeholder={placeholders.zip || 'Zip'}
            error={error}
            onChange={val => handleUpdateData('zip', zipFormat(val))}
          />
        </>
      )}

      {/* Street only (not full address) */}
      {isStreet && !isFullAddress && (
        <Field
          id="address"
          label={isBilling ? 'Billing Street:' : 'Street:'}
          value={fields.address || ''}
          placeholder={placeholders.address || 'Street Address'}
          required={isFieldRequired}
          title={isBilling ? 'Billing Street' : 'Street'}
          error={error}
          onChange={val => handleUpdateData('address', val)}
        />
      )}

      {/* Zip only (not full address) */}
      {isZip && !isFullAddress && (
        <Field
          id="zip"
          label={isBilling ? 'Billing Zip:' : 'Zip:'}
          value={fields.zip || ''}
          placeholder={placeholders.zip || 'Zip/Postal Code'}
          required={isFieldRequired}
          title={isBilling ? 'Billing Zip' : 'Zip'}
          error={error}
          onChange={val => handleUpdateData('zip', zipFormat(val))}
        />
      )}

      {isEmail && (
        <Field
          id="email"
          label="Email Address"
          type="email"
          value={fields.email || ''}
          placeholder={placeholders.email || 'Email Address'}
          required={isFieldRequired}
          error={error}
          onChange={val => handleUpdateData('email', val)}
        />
      )}

      {isPhone && (
        <Field
          id="phone"
          label="Phone Number"
          type="tel"
          value={fields.phone || ''}
          placeholder={placeholders.phone || 'Phone Number'}
          required={isFieldRequired}
          error={error}
          onChange={val => handleUpdateData('phone', val)}
        />
      )}
    </div>
  )
}

export default VerificationField
